import { NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChartBar, Package, Gear, SignOut, Airplane, HardDrives, IdentificationCard, Moon, IdentificationBadge, Receipt, FileText, Invoice, LockKey, Bank, Handshake, Folders, Camera,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: ChartBar, testid: "nav-overview" },
  { to: "/shipments", label: "Shipments", icon: Package, testid: "nav-shipments" },
  { to: "/nightly-report", label: "Nightly Report", icon: Moon, testid: "nav-nightly" },
  { to: "/permits", label: "Permits", icon: IdentificationBadge, testid: "nav-permits" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
  { to: "/tc-and-tcsr", label: "TC and TCSR", icon: FileText, testid: "nav-tc" },
  { to: "/purchase-inv", label: "Purchase INV", icon: Invoice, testid: "nav-pi" },
  { to: "/purchase-inv-2", label: "Purchase INV 2", icon: LockKey, testid: "nav-pi2" },
  { to: "/quotation", label: "Quotation", icon: Handshake, testid: "nav-quotation" },
  { to: "/bank-statement", label: "Bank Statement", icon: Bank, testid: "nav-bank" },
  { to: "/others", label: "Others", icon: Folders, testid: "nav-others" },
  { to: "/mobile-expenses", label: "Mobile Expenses", icon: Camera, testid: "nav-mobile-exp" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Punch-only workers must never see the Cargo Docs UI — send them to /punch.
  if (user?.role === "punch_worker") return <Navigate to="/punch" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Only admin has BOTH modules; show the switcher for admin & punch_admin
  const canSwitch = user?.role === "admin" || user?.role === "punch_admin";

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Sidebar */}
      <aside className="w-64 bg-zinc-950 text-white flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-6 py-6 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Airplane size={26} weight="duotone" className="text-[#FF4F00]" />
            <div>
              <div className="font-display font-black text-base tracking-tight leading-none">TOM <span className="text-[#FF4F00]">CARGO</span> DOCS</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Filing System</div>
            </div>
          </div>
        </div>

        {canSwitch && (
          <button
            onClick={() => navigate("/punch-admin")}
            data-testid="switch-to-punch-btn"
            className="mx-3 mt-3 px-3 py-2.5 flex items-center gap-2 text-sm border border-[#FF4F00] text-[#FF4F00] hover:bg-[#FF4F00] hover:text-white transition-colors duration-150"
            title="Open the Punch Card Admin dashboard"
          >
            <IdentificationCard size={18} weight="duotone" />
            <span>Switch → Punch Admin</span>
          </button>
        )}

        <nav className="flex-1 py-4 flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `mx-3 px-3 py-2.5 flex items-center gap-3 text-sm rounded-sm transition-colors duration-150 ${
                  isActive ? "bg-[#002FA7] text-white" : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-800 px-4 py-4">
          <div className="text-xs text-zinc-400 mb-2 flex items-center gap-1.5">
            <HardDrives size={12} />
            <span className="uppercase tracking-widest">Signed in</span>
          </div>
          <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate" data-testid="user-email">{user?.email}</div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-[#FF4F00]" data-testid="user-role">
            {user?.role}
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 text-sm bg-transparent border border-zinc-700 text-zinc-200 py-2 hover:bg-zinc-800 transition-colors duration-150"
          >
            <SignOut size={14} />
            Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">
        <div className="max-w-[1400px] mx-auto p-6 md:p-8">{children}</div>
      </main>
    </div>
  );
}
